import { useState, useEffect } from "react";
import { useServiceAreas } from "@/hooks/useServiceAreas";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Search, Plus, X, Star, Sparkles, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useReferralLeads } from "@/hooks/useReferralLeads";
import { isValidZip, filterZipInput, getEdgeFunctionErrorMessage } from "@/lib/validation";
import { REFERRAL_SOURCE_TYPES } from "@/lib/referral-sources";
import { HC_SPECIALTY_LABELS, GH_SPECIALTY_LABELS } from "@/lib/specialties";

const SOURCE_TYPES = REFERRAL_SOURCE_TYPES;

const RADIUS_OPTIONS = [
  { value: "0", label: "Exact ZIP" },
  { value: "25", label: "25 miles" },
  { value: "50", label: "50 miles" },
  { value: "75", label: "75 miles" },
  { value: "100", label: "100 miles" },
];

interface ReferralResult {
  org_name: string;
  contact_name?: string;
  title?: string;
  phone?: string;
  address?: string;
  zip_code?: string;
  source_type?: string;
  specialty_match?: string[];
  verified?: boolean;
  verified_place_id?: string;
  verified_name?: string;
  verified_address?: string;
  verified_phone?: string;
  verified_website?: string;
  verified_rating?: number;
  verified_rating_count?: number;
}

export default function FindReferrals() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const seg = (profile as any)?.segment || "homecare";
  const { serviceAreas, addServiceArea, updateServiceArea, deleteServiceArea } = useServiceAreas();
  const { addReferralLead } = useReferralLeads();
  const SPECIALTIES = seg === "grouphome" ? GH_SPECIALTY_LABELS : HC_SPECIALTY_LABELS;
  const [selectedZips, setSelectedZips] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [radiusMiles, setRadiusMiles] = useState("25");
  const [results, setResults] = useState<ReferralResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  const [newZip, setNewZip] = useState("");
  const [addingZip, setAddingZip] = useState(false);

  // Auto-select all service area ZIPs when they load or change
  useEffect(() => {
    if (serviceAreas.length > 0) {
      setSelectedZips(serviceAreas.map(sa => sa.zip_code));
    }
  }, [serviceAreas]);

  const toggleItem = (arr: string[], item: string, setter: (v: string[]) => void) => {
    setter(arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);
  };

  async function handleAddZip() {
    if (!isValidZip(newZip)) {
      toast.error("Enter a valid 5-digit ZIP code");
      return;
    }
    setAddingZip(true);
    try {
      await addServiceArea(newZip);
      // Auto-select the newly added ZIP
      setSelectedZips(prev => prev.includes(newZip) ? prev : [...prev, newZip]);
      setNewZip("");
      toast.success(`Added ZIP ${newZip}`);
    } catch (e: any) {
      if (e.message?.includes("duplicate")) toast.error("ZIP already added");
      else toast.error(e.message || "Failed to add ZIP");
    } finally {
      setAddingZip(false);
    }
  }

  async function handleSearch() {
    if (selectedZips.length === 0) {
      toast.error("Select at least one service area ZIP code before searching");
      return;
    }
    setSearching(true);
    setResults([]);
    try {
      const { data, error } = await supabase.functions.invoke("find-referrals", {
        body: {
          zip_codes: selectedZips,
          source_types: selectedSources,
          specialties: selectedSpecialties,
          radius_miles: parseInt(radiusMiles, 10),
        },
      });
      if (error) throw error;
      setResults(data.referrals || []);
      const status = data.verification_status as "ok" | "partial" | "skipped" | undefined;
      if (status === "skipped") {
        toast.warning("Verification unavailable — results are AI-suggested only.");
      } else if (status === "partial") {
        toast.warning("Some partners couldn't be verified — review carefully.");
      }
      if ((data.referrals || []).length === 0) toast.info("No verified referrals found. Try broadening your filters.");
    } catch (e: any) {
      toast.error(await getEdgeFunctionErrorMessage(e, "Search failed"));
    } finally {
      setSearching(false);
    }
  }

  async function handleSave(result: ReferralResult, idx: number) {
    if (!user) return;
    setSavingIdx(idx);
    try {
      await addReferralLead({
        user_id: user.id,
        org_name: result.org_name,
        contact_name: result.contact_name || null,
        title: result.title || null,
        phone: result.phone || null,
        address: result.address || null,
        zip_code: result.zip_code || null,
        source_type: result.source_type || null,
        specialty_match: result.specialty_match || [],
        status: "New",
      });
      toast.success(`Saved ${result.org_name} to outreach`);
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSavingIdx(null);
    }
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{seg === "grouphome" ? "Find Support Coordinators" : "Find Referral Partners"}</h2>
          <p className="text-sm text-muted-foreground">{seg === "grouphome" ? "AI-powered search for support coordinators and MCO care managers in your localities." : "AI-powered search for hospitals, rehab centers, and referral sources near your service areas."}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
          {/* Filters */}
          <div className="space-y-4 rounded-xl border bg-card p-4">
            <h3 className="text-sm font-semibold text-foreground">Filters</h3>

            {/* Manage Service Areas */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Service Areas</label>
              <div className="flex gap-2">
                <Input
                  placeholder="ZIP code"
                  type="text"
                  inputMode="numeric"
                  value={newZip}
                  onChange={e => setNewZip(filterZipInput(e.target.value))}
                  maxLength={5}
                  className="w-28"
                  onKeyDown={e => e.key === "Enter" && handleAddZip()}
                />
                <Button onClick={handleAddZip} disabled={addingZip} size="sm" variant="outline" className="gap-1">
                  {addingZip ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {serviceAreas.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No service areas yet</p>
                ) : (
                  serviceAreas.map(sa => (
                    <div key={sa.id} className="flex items-center gap-0.5 rounded-md border bg-secondary/50 px-2 py-1">
                      <Badge
                        variant={selectedZips.includes(sa.zip_code) ? "default" : "secondary"}
                        className="cursor-pointer text-xs px-1.5 py-0"
                        onClick={() => toggleItem(selectedZips, sa.zip_code, setSelectedZips)}
                      >
                        {sa.zip_code}
                      </Badge>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => updateServiceArea(sa.id, { is_golden_zone: !sa.is_golden_zone })}
                            className="ml-0.5"
                          >
                            <Star
                              className={`h-3.5 w-3.5 transition-colors ${
                                sa.is_golden_zone
                                  ? "fill-yellow-500 text-yellow-500"
                                  : "text-muted-foreground hover:text-yellow-500"
                              }`}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {sa.is_golden_zone ? "High-value area (click to remove)" : "Mark as high-value area"}
                        </TooltipContent>
                      </Tooltip>
                      <button onClick={() => deleteServiceArea(sa.id)} className="ml-0.5 text-muted-foreground hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Search Radius</label>
              <Select value={radiusMiles} onValueChange={setRadiusMiles}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RADIUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Source Type</label>
              <div className="flex flex-wrap gap-1">
                {SOURCE_TYPES.map(s => (
                  <Badge
                    key={s}
                    variant={selectedSources.includes(s) ? "default" : "secondary"}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleItem(selectedSources, s, setSelectedSources)}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Care Specialties</label>
              <div className="flex flex-wrap gap-1">
                {SPECIALTIES.map(s => (
                  <Badge
                    key={s}
                    variant={selectedSpecialties.includes(s) ? "default" : "secondary"}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleItem(selectedSpecialties, s, setSelectedSpecialties)}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>

            <Button onClick={handleSearch} className="w-full gap-2" disabled={searching || selectedZips.length === 0}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {searching ? "Searching…" : "Find Referrals"}
            </Button>
            {selectedZips.length === 0 && serviceAreas.length > 0 && (
              <p className="text-xs text-destructive">Select at least one ZIP code to search</p>
            )}
          </div>

          {/* Results */}
          <div className="rounded-xl border bg-card overflow-hidden">
            {results.length === 0 ? (
              <div className="flex items-center justify-center p-12 text-muted-foreground">
                {searching ? "Searching for referral partners…" : "Use the filters to find referral partners"}
              </div>
            ) : (
              <>
              <div className="flex items-start gap-2 border-b bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <span>
                  AI-suggested partners, cross-checked against Google Places. Verified entries
                  show a green badge — always confirm details before reaching out.
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>ZIP</TableHead>
                    <TableHead>Specialties</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        {r.verified_name || r.org_name}
                        {r.verified_website && (
                          <div className="text-xs">
                            <a
                              href={r.verified_website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              Website
                            </a>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.contact_name && <div>{r.contact_name}</div>}
                        {r.title && <div className="text-xs text-muted-foreground">{r.title}</div>}
                        {(r.verified_phone || r.phone) && (
                          <div className="text-xs text-muted-foreground">{r.verified_phone || r.phone}</div>
                        )}
                      </TableCell>
                      <TableCell>{r.source_type || "—"}</TableCell>
                      <TableCell>{r.zip_code || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(r.specialty_match || []).map(s => (
                            <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.verified ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="gap-1 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-400">
                                <ShieldCheck className="h-3 w-3" />
                                Verified
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {r.verified_address && <div className="text-xs">{r.verified_address}</div>}
                              {typeof r.verified_rating === "number" && (
                                <div className="text-xs">
                                  ★ {r.verified_rating.toFixed(1)}
                                  {r.verified_rating_count ? ` (${r.verified_rating_count} reviews)` : ""}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground">Confirmed via Google Places</div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" className="gap-1">
                                <ShieldAlert className="h-3 w-3" />
                                Unverified
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Could not confirm this organization with Google Places.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => handleSave(r, i)} disabled={savingIdx === i}>
                          {savingIdx === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                          Save
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
